import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, takeNext } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneLogin from "@/components/PhoneLogin";
import { GoogleButton, OrDivider } from "@/components/AuthCallback";
import { useConfig } from "@/lib/config";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const cfg = useConfig();
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const afterLogin = async () => {
    const { resumePendingPlan } = await import("@/components/Pricing");
    if (await resumePendingPlan()) return;
    nav(takeNext());
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const { resumePendingPlan } = await import("@/components/Pricing");
      if (await resumePendingPlan()) return;
      nav(takeNext());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex items-center justify-center bg-muted border-r border-border paper-noise">
        <div className="max-w-md px-10">
          <div className="font-mono-label text-muted-foreground mb-6">Welcome back</div>
          <h1 className="font-serif-display text-5xl tracking-tighter leading-none mb-6">
            Pick up where<br />your reading<br />left off.
          </h1>
          <p className="text-muted-foreground">Every document, every citation, every chat — exactly where you parked it.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <Link to="/" className="inline-flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-foreground text-background grid place-items-center font-serif-display text-lg font-black">D</div>
            <span className="font-serif-display text-xl">DocuMind</span>
          </Link>
          <div>
            <h2 className="font-serif-display text-3xl mb-2">Log in</h2>
            <p className="text-sm text-muted-foreground">Don't have an account? <Link to="/signup" className="underline">Sign up</Link></p>
          </div>
          {cfg?.sms_enabled && (
            <div className="inline-flex border border-foreground" data-testid="login-mode-toggle">
              {[["email", "Email"], ["phone", "Text me a code"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`login-mode-${k}`} onClick={() => setMode(k)}
                  className={`font-mono-label px-4 py-2 ${mode === k ? "bg-foreground text-background" : "hover:bg-muted"}`}>{l}</button>
              ))}
            </div>
          )}
          {mode === "phone" ? <PhoneLogin onSuccess={afterLogin} /> : (
          <>
          <GoogleButton />
          <OrDivider />
          <form onSubmit={submit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="font-mono-label">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="font-mono-label">Password</Label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1.5 h-11" />
            </div>
          </div>
          <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 bg-foreground text-background hover:bg-primary rounded-sm btn-invert">
            {loading ? "Signing in…" : "Log in →"}
          </Button>
          </form>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
